import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select } from '@artosera/ui';
import type { ModeReglement } from '@artosera/shared';
import { useAjouterReglement } from '../../api/ventes.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import styles from '../ventes/FactureDetailPage.module.css';

const MODES_REGLEMENT_UI: readonly ModeReglement[] = [
  'virement',
  'carte',
  'cheque',
  'especes',
  'autre',
];

export function EnregistrerReglementDialog({
  galerieSlug,
  factureId,
  devise = 'EUR',
  langue,
  initial,
  onClose,
}: {
  galerieSlug: string;
  factureId: string;
  devise?: string;
  langue: 'fr' | 'en';
  initial: { montantCentimes: number; date: string; reference?: string };
  onClose: () => void;
}) {
  const { t } = useTranslation(['regie', 'ventes', 'commun']);
  const ajouter = useAjouterReglement(galerieSlug);
  const [montant, setMontant] = useState(initial.montantCentimes);
  const [mode, setMode] = useState<ModeReglement>('virement');
  const [date, setDate] = useState(initial.date);
  const [reference, setReference] = useState(initial.reference ?? '');
  const [erreur, setErreur] = useState<string | null>(null);

  async function soumettre(): Promise<void> {
    setErreur(null);
    try {
      await ajouter.mutateAsync({
        factureId,
        corps: { montantCentimes: montant, mode, date, ...(reference ? { reference } : {}) },
      });
      onClose();
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={t('regie:ventes.fiche.ajouterReglement')}
      closeLabel={t('commun:actions.fermer')}
      footer={
        <>
          <Button variant="ghost" onClick={() => onClose()}>
            {t('commun:actions.annuler')}
          </Button>
          <Button onClick={() => void soumettre()} loading={ajouter.isPending}>
            {t('commun:actions.ajouter')}
          </Button>
        </>
      }
    >
      <div className={styles.formulaireReglement}>
        <Field label={t('regie:ventes.fiche.montantChamp')} htmlFor="reglement-montant">
          <PriceInput
            id="reglement-montant"
            cents={montant}
            onCentsChange={setMontant}
            lang={langue}
            currency={devise}
          />
        </Field>
        <Field label={t('regie:ventes.fiche.modeChamp')} htmlFor="reglement-mode">
          <Select
            id="reglement-mode"
            aria-label={t('regie:ventes.fiche.modeChamp')}
            value={mode}
            onValueChange={(valeur) => setMode(valeur as ModeReglement)}
            options={MODES_REGLEMENT_UI.map((code) => ({
              value: code,
              label: t(`ventes:reglements.${code}`),
            }))}
          />
        </Field>
        <Field label={t('regie:ventes.fiche.dateChamp')} htmlFor="reglement-date">
          <input
            id="reglement-date"
            type="date"
            className={styles.saisieDate}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </Field>
        <Field label={t('regie:tresorerie.referenceReglement')} htmlFor="reglement-reference">
          <Input
            id="reglement-reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </Field>
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
